import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Button, Link } from '@mui/material';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import PostLoginLoader from '../components/PostLoginLoader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';
const ERS_URL = (process.env.REACT_APP_ERS_URL || '').replace(/\/$/, '');

function buildApiBase() {
  const raw = API_URL.trim().replace(/\/+$/, '');
  return raw.endsWith('/api') ? raw : `${raw}/api`;
}

/**
 * Complete Supabase redirect (PKCE ?code= or hash tokens), then bridge to Envoy JWT.
 */
async function completeSupabaseRedirect() {
  if (!supabase) {
    throw new Error('Supabase is not configured on this environment.');
  }

  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');

  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
  } else if (window.location.hash) {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const access_token = hash.get('access_token');
    const refresh_token = hash.get('refresh_token');
    if (access_token && refresh_token) {
      const { error } = await supabase.auth.setSession({ access_token, refresh_token });
      if (error) throw error;
    }
  }

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  if (!session?.access_token) {
    throw new Error('No Supabase session found. The sign-in link may have expired.');
  }

  return session;
}

const AuthCallback = () => {
  const navigate = useNavigate();
  const { establishAppSession } = useAuth();
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [ersUrl, setErsUrl] = useState(ERS_URL);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const session = await completeSupabaseRedirect();
        const base = buildApiBase();

        const response = await fetch(`${base}/auth/supabase-bridge`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          const err = new Error(payload.message || 'Failed to sign in via magic link');
          err.code = payload.code;
          err.ersUrl = payload.ersUrl;
          throw err;
        }

        const { token, user } = payload.data || {};
        if (!token || !user) {
          throw new Error('Invalid response from sign-in bridge');
        }

        if (cancelled) return;

        establishAppSession({ token, user });
        window.history.replaceState({}, document.title, '/auth/callback');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Sign-in failed');
          setErrorCode(err.code || '');
          if (err.ersUrl) setErsUrl(err.ersUrl);
          setProcessing(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [establishAppSession, navigate]);

  if (processing) {
    return <PostLoginLoader />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" gutterBottom>
          Sign-in failed
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            {errorCode === 'DASHBOARD_ACCESS_DENIED' && ersUrl && (
              <Box sx={{ mt: 1 }}>
                <Link href={ersUrl} underline="hover" sx={{ fontWeight: 700 }}>
                  Open the Employee Roster System
                </Link>
              </Box>
            )}
          </Alert>
        )}
        <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
          Go to login
        </Button>
      </Box>
    </Box>
  );
};

export default AuthCallback;
